/**
 * Shared spreadsheet utilities for cleaning Excel/CSV data.
 *
 * Used by both the frontend manual upload flow and backend workflow
 * steps to ensure consistent data cleaning behavior.
 *
 * The approach: SheetJS `raw: false` gives formatted values (readable dates,
 * formatted numbers). These functions then clean formatted numbers back to
 * actual numbers while preserving dates and text.
 */

/**
 * Clean and parse Excel formatted values, focusing on formatted numbers.
 *
 * Uses raw: false output from SheetJS where:
 * - Numbers come as "1,000" (formatted with commas — needs cleaning)
 * - Dates come as "2024-01-01" (human readable — preserved as-is)
 *
 * Conservative criteria — ALL must be true to clean a value:
 * - Matches strict regex patterns for formatted numbers
 * - Contains only valid number characters
 * - Has ≤ 3 commas, no consecutive commas, no leading/trailing commas
 * - Has ≤ 1 decimal point (rejects European format like 1.000.000)
 *
 * Examples:
 * ✅ "1,000" → 1000
 * ✅ "$1,000" → 1000
 * ✅ "1,000 CAD" → 1000
 * ✅ "-1,000" → -1000
 * ✅ "(1,000)" → -1000 (accounting format)
 * ✅ "25%" → 0.25
 * ✅ "2024-01-01" → "2024-01-01" (date — preserved)
 * ✅ "1/1/2024" → "1/1/2024" (date — preserved)
 * ✅ "1000CAD" → "1000CAD" (no space — preserved as text)
 */
export const cleanExcelValue = (value: unknown): unknown => {
  if (typeof value === "string") {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
      return value;
    }

    // Percentage format: 25% = 0.25
    const percentageFormatRegex = /^[\d,]+\.?\d*%$/;
    if (percentageFormatRegex.test(trimmedValue)) {
      const cleanedValue = trimmedValue.replace(/%/g, "").replace(/,/g, "");
      const parsedNumber = parseFloat(cleanedValue);
      if (!isNaN(parsedNumber)) {
        return parsedNumber / 100;
      }
    }

    // Negative numbers: -1,000 = -1000
    const negativeNumberRegex = /^-[\d,]+\.?\d*$/;
    if (negativeNumberRegex.test(trimmedValue)) {
      const cleanedValue = trimmedValue.replace(/,/g, "");
      const parsedNumber = parseFloat(cleanedValue);
      if (!isNaN(parsedNumber)) {
        return parsedNumber;
      }
    }

    // Accounting format: (1,000) = -1000, ($ 773.44) = -773.44, ($$ 773.44) = -773.44
    const accountingFormatRegex = /^\([$€£¥]*\s*[\d,]+\.?\d*\)$/;
    if (accountingFormatRegex.test(trimmedValue)) {
      const cleanedValue = trimmedValue
        .replace(/[(),$€£¥,]/g, "")
        .replace(/\s/g, "");
      const parsedNumber = parseFloat(cleanedValue);
      if (!isNaN(parsedNumber)) {
        return -parsedNumber;
      }
    }

    // Formatted numbers with optional currency
    const cleanFormattedNumberRegex = /^[\d,]+\.?\d*$/;
    const cleanCurrencyPrefixRegex = /^[$€£¥]+\s*[\d,]+\.?\d*$/;
    const cleanCurrencySuffixRegex =
      /^[\d,]+\.?\d*\s+(CAD|USD|EUR|GBP|JPY|AUD|CHF|CNY|INR|BRL|MXN|RUB|ZAR|KRW|SGD|HKD|NZD)$/i;

    const hasOnlyValidChars = /^[\d,.\s$€£¥A-Za-z]*$/.test(trimmedValue);
    const hasReasonableCommaCount =
      (trimmedValue.match(/,/g) || []).length <= 3;
    const noConsecutiveCommas = !/,{2,}/.test(trimmedValue);
    const noLeadingTrailingCommas = !/^,|,$/.test(trimmedValue);
    const hasOnlyOneDecimalPoint =
      (trimmedValue.match(/\./g) || []).length <= 1;

    if (
      (cleanFormattedNumberRegex.test(trimmedValue) ||
        cleanCurrencyPrefixRegex.test(trimmedValue) ||
        cleanCurrencySuffixRegex.test(trimmedValue)) &&
      hasOnlyValidChars &&
      hasReasonableCommaCount &&
      noConsecutiveCommas &&
      noLeadingTrailingCommas &&
      hasOnlyOneDecimalPoint
    ) {
      const cleanedValue = trimmedValue
        .replace(/[,$€£¥\s]/g, "")
        .replace(
          /(CAD|USD|EUR|GBP|JPY|AUD|CHF|CNY|INR|BRL|MXN|RUB|ZAR|KRW|SGD|HKD|NZD)$/i,
          "",
        );
      const parsedNumber = parseFloat(cleanedValue);
      if (!isNaN(parsedNumber)) {
        return parsedNumber;
      }
    }

    return value;
  }

  return value;
};

/**
 * Clean all values in a 2D array using cleanExcelValue.
 */
export const cleanDataValues = (data: unknown[][]): unknown[][] => {
  return data.map((row) => row.map((cell) => cleanExcelValue(cell)));
};

/**
 * Filter out completely empty rows and columns from spreadsheet data.
 */
export const filterEmptyRowsAndColumns = (data: unknown[][]): unknown[][] => {
  if (data.length === 0) return data;

  // Filter out empty rows
  const rowsFiltered = data.filter((row) => {
    return row.some((cell) => {
      if (cell === null || cell === undefined) return false;
      if (typeof cell === "string") return cell.trim().length > 0;
      return true;
    });
  });

  if (rowsFiltered.length === 0) return rowsFiltered;

  // Find non-empty columns
  const maxColumns = Math.max(...rowsFiltered.map((row) => row.length));
  const columnsToKeep: number[] = [];
  for (let colIndex = 0; colIndex < maxColumns; colIndex++) {
    const hasNonEmptyCell = rowsFiltered.some((row) => {
      const cell = row[colIndex];
      if (cell === null || cell === undefined) return false;
      if (typeof cell === "string") return cell.trim().length > 0;
      return true;
    });
    if (hasNonEmptyCell) {
      columnsToKeep.push(colIndex);
    }
  }

  // Filter out empty columns
  return rowsFiltered.map((row) => {
    return columnsToKeep.map((colIndex) => {
      const cell = row[colIndex];
      return cell !== undefined && cell !== null ? cell : "";
    });
  });
};

/**
 * Full pipeline: clean Excel values then filter empty rows/columns.
 * Use this after SheetJS `sheet_to_json(sheet, { header: 1, defval: '', raw: false })`.
 */
export const cleanAndFilterSpreadsheetData = (
  data: unknown[][],
): unknown[][] => {
  return filterEmptyRowsAndColumns(cleanDataValues(data));
};
